import clsx from "clsx"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BoxIcon, GoldenDustImg } from "@/assets"
import { CurrencyIcon } from "@/helpers/currencyIcon"
import { spaceNumber } from "@/helpers/spaceNumber"
import { useModals } from "@/modals"
import { useAppSelector } from "@/store"
import {
  CARD_SKIN_TYPES,
  useGetCardBackTemplateByTypeQuery
} from "@/store/apis/cardBacksAPI"
import { useGetPersonalDeckInventoryCountsQuery } from "@/store/apis/personalDeckAPI"
import { IStoreItem, STORE_ITEM_TYPES } from "@/store/apis/storeAPI"
import { STORE_CURRENCY } from "@/store/apis/storePaymentsAPI"
import { AppSelectors } from "@/store/slices/app"
import { Button } from "@/ui/Button"
import { CardView } from "@/ui/Card"
import styles from "./GoldCardItem.module.scss"

interface IGoldCardItem extends IStoreItem<STORE_ITEM_TYPES.CARD> {}

export const GoldCardItem: FC<IGoldCardItem> = (cardItem) => {
  const { dataId, data, pricing } = cardItem

  const { t } = useTranslation("cards")
  const { openModal } = useModals()

  const storeCurrency = useAppSelector(AppSelectors.selectStoreCurrency())

  const { data: cardsInventoryCounts } =
    useGetPersonalDeckInventoryCountsQuery()
  const { data: goldCardTemplate } = useGetCardBackTemplateByTypeQuery(
    CARD_SKIN_TYPES.GOLD
  )

  const cardsInventory = cardsInventoryCounts as
    | Record<string, number>
    | undefined

  const cardCount = cardsInventory?.[dataId] ?? 0

  const btnText = useMemo(() => {
    if (storeCurrency === STORE_CURRENCY.TG_STARS) {
      return (
        <>
          <CurrencyIcon.star /> {spaceNumber(pricing.stars)}
        </>
      )
    }

    if (storeCurrency === STORE_CURRENCY.GOLDEN_DUST) {
      return (
        <>
          <img src={GoldenDustImg} /> {spaceNumber(pricing.goldenDust)}
        </>
      )
    }

    return (
      <>
        <CurrencyIcon.ton /> {spaceNumber(pricing.ton)}
      </>
    )
  }, [pricing, storeCurrency])

  const handleBuy = () => {
    openModal("buyGoldCard", cardItem)
  }

  return (
    <div className={clsx(styles.CardSkinItem)} onClick={handleBuy}>
      <div className={clsx(styles.CardSkinItem_header)}>
        {/* <div className={clsx(styles.CardSkinItem_title)}>
          {t(`card.${data?.type}.title.nominative`, {
            defaultValue: data?.type
          })}
        </div> */}
        <div className={clsx(styles.CardSkinItem_img)}>
          <CardView
            className={clsx(styles.CardSkinItem_preview)}
            data={{ ...data, price: 0 }}
            title={t(`card.${data?.type}.title.nominative`)}
            description={t(`card.${data?.type}.description_short`)}
            skin={goldCardTemplate}
            isShowBackSide={false}
            isMatchActive={true}
          />
        </div>
        <div className={clsx(styles.CardSkinItem_count)}>
          <BoxIcon /> {spaceNumber(cardCount)}
        </div>
      </div>
      <Button
        className={clsx(styles.CardSkinItem_button, {
          [styles.star]: storeCurrency === STORE_CURRENCY.TG_STARS,
          [styles.ton]: storeCurrency === STORE_CURRENCY.EXTERNAL_TON,
          [styles.goldenDust]: storeCurrency === STORE_CURRENCY.GOLDEN_DUST
        })}
      >
        {btnText}
      </Button>
    </div>
  )
}
